import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';

/**
 * Who has been paid for what, container by container.
 *
 * One order can hold several invoices, and each payment is made against one of
 * them for SOME of its containers. A payment now says which, so "100% Full
 * Payment" on a 3-container invoice no longer reads as the whole order paid.
 * (The rule itself lives in backend services/procurement/payment-coverage.ts.)
 */

export const TRANCHE_PCT: Record<string, number> = { Advance_30: 30, Advance_50: 50, Balance_70: 70, Balance_50: 50, Full_Payment: 100 };
export const isPriceTranche = (t: string) => t in TRANCHE_PCT;
const TYPE_LABEL: Record<string, string> = { Advance_30: '30% advance', Advance_50: '50% advance', Balance_70: '70% balance', Balance_50: '50% balance', Full_Payment: '100% payment' };

export interface CovUnit {
  id: string; containerNo: string; label: string; rate: number; currency: string; requestedPct: number; paidPct: number;
  payments: Array<{ paymentId: string; type: string; status: string; invoiceNumber: string; legacy: boolean }>;
}
export interface Coverage {
  indentId: string; scoped: boolean; units: CovUnit[]; legacyPaymentIds: string[];
  summary: { total: number; fullyPaid: number; partlyPaid: number; notPaid: number; requestedNotPaid: number; paidPercent: number; requestedPercent: number };
}

/** A container can take each tranche once, and its tranches may not exceed 100%. */
export function unitBlocked(u: CovUnit, type: string, ignorePaymentId?: string): boolean {
  const pct = TRANCHE_PCT[type] || 0;
  const others = u.payments.filter((x) => x.paymentId !== ignorePaymentId);
  return others.some((x) => x.type === type) || others.reduce((a, x) => a + (TRANCHE_PCT[x.type] || 0), 0) + pct > 100;
}

const money = (n: number, cur: string) => `${cur === 'INR' ? '₹' : `${cur} `}${Math.round(n).toLocaleString('en-IN')}`;

function unitNote(u: CovUnit, ignorePaymentId?: string): string {
  const ps = u.payments.filter((x) => x.paymentId !== ignorePaymentId);
  if (!ps.length) return 'not requested yet';
  return ps.map((p) => `${TYPE_LABEL[p.type] || p.type} ${p.status === 'paid' ? 'paid' : p.status === 'partially_paid' ? 'part-paid' : 'requested'}${p.invoiceNumber ? ` · ${p.invoiceNumber}` : ''}${p.legacy ? ' · whole order' : ''}`).join(' + ');
}

/** Tick the containers a payment is for. */
export function UnitPicker({ coverage, type, picked, onChange, ignorePaymentId }: {
  coverage: Coverage; type: string; picked: Set<string>; onChange: (s: Set<string>) => void; ignorePaymentId?: string;
}) {
  const free = coverage.units.filter((u) => !unitBlocked(u, type, ignorePaymentId));
  const allFree = free.length > 0 && free.every((u) => picked.has(u.id));
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Containers this payment is for <span className="text-red-500">*</span></label>
        {free.length > 0 && (
          <button type="button" className="text-[11px] font-semibold text-teal-700 hover:underline"
            onClick={() => onChange(allFree ? new Set() : new Set(free.map((u) => u.id)))}>
            {allFree ? 'Clear' : `Select all available (${free.length})`}
          </button>
        )}
      </div>
      <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-slate-200 bg-white">
        {coverage.units.map((u) => {
          const blocked = unitBlocked(u, type, ignorePaymentId);
          return (
            <label key={u.id} className={`flex items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 text-xs last:border-b-0 ${blocked ? 'bg-slate-50 text-slate-400' : 'cursor-pointer hover:bg-teal-50/40'}`}>
              <input type="checkbox" className="h-3.5 w-3.5 accent-teal-600" disabled={blocked} checked={!blocked && picked.has(u.id)}
                onChange={() => { const n = new Set(picked); if (n.has(u.id)) n.delete(u.id); else n.add(u.id); onChange(n); }} />
              <span className="font-mono font-semibold">{u.label}</span>
              {u.rate > 0 && <span className="text-slate-400">{money(u.rate, u.currency)}</span>}
              <span className={`ml-auto text-[10.5px] ${blocked ? 'text-amber-700' : 'text-slate-400'}`}>{blocked ? `already: ${unitNote(u, ignorePaymentId)}` : unitNote(u, ignorePaymentId)}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** "3 of 6 containers" chip for a payment row. */
export function ScopeChip({ payment, coverage }: { payment: any; coverage?: Coverage | null }) {
  if (!coverage?.scoped || !isPriceTranche(String(payment.paymentType))) return null;
  const ids: string[] = payment.unitIds || [];
  const total = coverage.units.length;
  if (!ids.length) {
    return <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" title="This payment does not say which containers it covers, so it counts as the whole order.">whole order · not itemised</span>;
  }
  const names = coverage.units.filter((u) => ids.includes(u.id)).map((u) => u.label);
  return <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold text-teal-800" title={names.join(', ')}>{ids.length} of {total} containers</span>;
}

/** Ledger strip: how much of the order is paid, container by container. */
export function PaymentCoverageStrip({ coverage, payments, indentId, onRequest }: { coverage?: Coverage | null; payments: any[]; indentId: string; onRequest: () => void }) {
  const [open, setOpen] = useState(false);
  const [fixing, setFixing] = useState<any | null>(null);
  if (!coverage?.scoped) return null;
  const s = coverage.summary;
  const hasPrice = payments.some((p) => isPriceTranche(String(p.paymentType)) && p.status !== 'rejected');
  if (!hasPrice && coverage.units.length < 2) return null;
  const notRequested = coverage.units.filter((u) => u.requestedPct <= 0.1).length;
  const legacy = payments.filter((p) => coverage.legacyPaymentIds.includes(String(p._id)));
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="min-w-[220px] flex-1">
          <div className="text-sm font-bold text-slate-800">
            Container price paid: {s.fullyPaid} of {s.total} fully paid
            {s.partlyPaid > 0 && <span className="font-normal text-slate-500"> · {s.partlyPaid} part-paid</span>}
          </div>
          <div className="mt-1.5 h-2 w-full rounded-full bg-slate-100" title={`${s.paidPercent}% of the order's value is paid, ${s.requestedPercent}% asked for`}>
            <div className="relative h-2 rounded-full bg-amber-300" style={{ width: `${Math.min(100, s.requestedPercent)}%` }}>
              <div className="absolute inset-y-0 left-0 rounded-full bg-emerald-500" style={{ width: s.requestedPercent > 0 ? `${Math.min(100, (s.paidPercent / s.requestedPercent) * 100)}%` : '0%' }} />
            </div>
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            {s.paidPercent}% of the order's value paid · {s.requestedPercent}% asked for · {notRequested} container{notRequested === 1 ? '' : 's'} with no payment requested
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="text-xs font-semibold text-teal-700 hover:underline" onClick={() => setOpen((v) => !v)}>{open ? 'Hide containers' : 'Show containers'}</button>
          {notRequested > 0 && <button className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700" onClick={onRequest}>Request payment for the other {notRequested}</button>}
        </div>
      </div>
      {legacy.length > 0 && (
        <div className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-[11.5px] text-amber-900">
          {legacy.map((p) => (
            <div key={p._id} className="flex flex-wrap items-center gap-2">
              <span><b>{TYPE_LABEL[p.paymentType] || p.paymentType}</b>{p.invoiceNumber ? ` (${p.invoiceNumber})` : ''} doesn't say which containers it is for, so it is counted as the <b>whole order</b>.</span>
              <button className="font-semibold underline" onClick={() => setFixing(p)}>Choose its containers</button>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="border-t border-slate-100">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-slate-400"><th className="px-4 py-1.5">Container</th><th className="text-right">Price</th><th className="px-3">Paid</th><th>Payments</th></tr></thead>
            <tbody>
              {coverage.units.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-4 py-1.5 font-mono font-semibold">{u.label}</td>
                  <td className="text-right tabular-nums text-slate-500">{u.rate > 0 ? money(u.rate, u.currency) : '—'}</td>
                  <td className="px-3">
                    <span className={u.paidPct >= 99.9 ? 'font-semibold text-emerald-700' : u.paidPct > 0 ? 'font-semibold text-amber-700' : 'text-slate-400'}>{Math.round(u.paidPct)}%</span>
                    {u.requestedPct > u.paidPct + 0.1 && <span className="ml-1 text-[10.5px] text-amber-700">({Math.round(u.requestedPct)}% asked)</span>}
                  </td>
                  <td className="text-slate-600">{u.payments.length ? unitNote(u) : <span className="text-slate-400">none requested</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {fixing && <SetContainersModal payment={fixing} coverage={coverage} indentId={indentId} onClose={() => setFixing(null)} />}
    </div>
  );
}

/** Say which containers an existing payment covers. */
export function SetContainersModal({ payment, coverage, indentId, onClose }: { payment: any; coverage: Coverage; indentId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(new Set(payment.unitIds?.length ? payment.unitIds : []));
  const save = useMutation({
    mutationFn: () => api.post(`/api/procurement/payments/${payment._id}/containers`, { unitIds: [...picked] }),
    onSuccess: () => {
      toast.success('Containers saved');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'Could not save'),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-bold text-slate-800">Which containers is this payment for?</div>
        <p className="mb-2 mt-0.5 text-[11.5px] text-slate-500">
          {TYPE_LABEL[payment.paymentType] || payment.paymentType}{payment.invoiceNumber ? ` · invoice ${payment.invoiceNumber}` : ''}. Tick only the containers on the invoice this payment settled — the rest stay unpaid and can be requested separately.
        </p>
        <UnitPicker coverage={coverage} type={payment.paymentType} picked={picked} onChange={setPicked} ignorePaymentId={String(payment._id)} />
        <div className="mt-3 flex justify-end gap-2">
          <button className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600" onClick={onClose}>Cancel</button>
          <button className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" disabled={!picked.size || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : `Save ${picked.size} container${picked.size === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
